import {
  useStats,
  useMatchDetails,
  useLatestPractice,
  useCanReportBatch,
} from "@/lib/hooks/useApi";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useNextStep } from "nextstepjs";
import {
  Activity,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePrivy } from "@privy-io/react-auth";
import {
  cn,
  generatePrizeData,
  generateTextShadow,
  REWARD_ICONS,
} from "@/lib/utils";
import { useSolanaPrice } from "@/lib/hooks/useSolanaPrice";
import RollingList from "@/components/RollingList";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import ResourceBar from "@/components/ResourceBar";
import { ClaimBorderDialog } from "@/components/ClaimBorderDialog";
import { DefaultButton } from "@/components/DefaultButton";
import { AvatarBorder } from "@/components/AvatarBorder";
import {
  isRushLeaderboardFlipped,
  RUSH_GRAND_RUSH_REWARD_SWAP_AT,
} from "@/lib/constants";
import { useEpochReached } from "@/lib/hooks/useEpochReached";
import { t } from "@/lib/i18n";
import type {
  Match,
  Player,
  PracticePlayerJoinedEvent,
  PracticeMatchStatusChangedEvent,
} from "@/lib/types/api";
import configs from "@/lib/configs";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import AnimatedLoading from "@/components/AnimatedLoading";
import { ReportPlayerDialog } from "@/components/ReportPlayerDialog";
import { HowItWorkButton } from "@/components/ui/HowItWorkButton";
import { NavigationPractice } from "./-components/NavigationPractice";
import { PracticeLive } from "./-components/PracticeLive";
import { PracticeEnded } from "./-components/PracticeEnded";
import { useSetAtom } from "jotai";
import { isGameLiveAtom } from "@/lib/gameStatusAtom";
import { MatchWaiting } from "./-components/MatchWaiting";

export const Route = createFileRoute("/(game)/practice/")({
  component: Practice,
});

type Phase = "idle" | "waiting" | "live" | "completed";

const SESSION_STORAGE_KEY = "cfl:practice-match-id";

function Practice() {
  const { user } = usePrivy();
  const router = useRouter();
  const { startNextStep, closeNextStep } = useNextStep();

  const [matchData, setMatchData] = useState<Match | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [isSseErrorOpen, setIsSseErrorOpen] = useState(false);
  const [displayedPlayers, setDisplayedPlayers] = useState<Player[]>([]);

  const setIsGameLive = useSetAtom(isGameLiveAtom);
  useEffect(() => {
    setIsGameLive(phase === "live");
    return () => setIsGameLive(false);
  }, [phase, setIsGameLive]);

  // Auto-start practice tour when arriving from lobby onboarding
  useEffect(() => {
    if (!user) return;
    const pending = sessionStorage.getItem("cfl:lobby-tour-practice-pending");
    if (!pending) return;

    const timer = setTimeout(() => {
      startNextStep("practiceTour");
    }, 500);
    return () => clearTimeout(timer);
  }, [user, startNextStep]);

  // Clear lobby tour flags on logout
  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem("cfl:lobby-tour-practice-pending");
      sessionStorage.removeItem("cfl:lobby-tour-squad-pending");
    }
  }, [user]);

  const { data: userStats, refetch: refetchUserStats } = useStats(
    user?.wallet?.address ?? "",
  );
  const { data: latestPractice } = useLatestPractice();
  const hasRewardSwapped = useEpochReached(RUSH_GRAND_RUSH_REWARD_SWAP_AT);
  const { data: solPrice } = useSolanaPrice();

  // --- Stagger latestPractice players one at a time on page load ---
  const latestPlayers = latestPractice?.players;
  const latestPlayersCount = latestPlayers?.length ?? 0;

  useEffect(() => {
    if (!latestPlayersCount || phase !== "idle") return;

    console.log(
      "[stagger idle] waiting for RollingList to settle before staggering",
      latestPlayersCount,
      "players",
    );
    let index = 0;
    let interval: ReturnType<typeof setInterval> | null = null;

    const addNext = () => {
      if (index >= latestPlayersCount) {
        console.log("[stagger idle] done — all players displayed");
        if (interval) clearInterval(interval);
        return;
      }
      const player = latestPlayers![index];
      console.log("[stagger idle] adding player", index, player.squadAddress);
      setDisplayedPlayers((prev) => [...prev, player]);
      index++;
    };

    // Wait ~3.5s for RollingList initial spin to finish, then stagger players
    const delay = setTimeout(() => {
      console.log("[stagger idle] RollingList settled, starting stagger");
      addNext();

      interval = setInterval(() => {
        if (index >= latestPlayersCount) {
          clearInterval(interval!);
          return;
        }
        addNext();
      }, 1000);
    }, 3500);

    return () => {
      console.log("[stagger idle] cleanup");
      clearTimeout(delay);
      if (interval) clearInterval(interval);
    };
  }, [latestPlayersCount, phase]);

  const practiceEntryFee = latestPractice?.entryUsdc ?? 0;
  const joinedPlayers = matchData?.players?.length ?? displayedPlayers.length;

  const prizeData = useMemo(
    () => generatePrizeData(practiceEntryFee, joinedPlayers, solPrice),
    [practiceEntryFee, joinedPlayers, solPrice],
  );

  const joinSoundRef = useRef<HTMLAudioElement>(null);
  const [isPiggyActive, setIsPiggyActive] = useState(false);
  const prevPlayersCountRef = useRef<number | null>(null);
  const [reportTarget, setReportTarget] = useState<Player | null>(null);

  const practiceReportedIds = reportTarget?.ownerProfile.userId != null
    ? [reportTarget.ownerProfile.userId]
    : [];
  const { data: practiceCanReportStatuses } = useCanReportBatch(
    practiceReportedIds,
    userStats?.userId,
  );
  const practiceCanReportMap = new Map(
    practiceCanReportStatuses?.map((s) => [s.reportedId, s.canReport]) ?? [],
  );

  // --- Reconnection: check sessionStorage for an active match on mount ---
  const savedMatchId =
    typeof window !== "undefined"
      ? sessionStorage.getItem(SESSION_STORAGE_KEY)
      : null;
  const { data: restoredMatch } = useMatchDetails(savedMatchId ?? "");

  useEffect(() => {
    if (restoredMatch && phase === "idle" && !matchData) {
      const status = restoredMatch.status;
      if (status === "waiting" || status === "live") {
        setMatchData(restoredMatch as Match);
        setPhase(status === "waiting" ? "waiting" : "live");
      } else if (status === "completed") {
        setMatchData(restoredMatch as Match);
        setPhase("completed");
      } else {
        // Match is no longer active — clear stale id
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    }
  }, [restoredMatch, phase, matchData]);

  // --- Persist matchId to sessionStorage ---
  useEffect(() => {
    if (matchData?.matchId) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, matchData.matchId);
    }
  }, [matchData?.matchId]);

  // --- Clear matchId when returning to idle ---
  useEffect(() => {
    if (phase === "idle") {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, [phase]);

  // --- SSE connection for lobby & live phases ---
  useEffect(() => {
    if (!matchData?.matchId || phase === "idle" || phase === "completed")
      return;

    const eventSource = new EventSource(
      `${configs.API_BASE_URL}/sse/practice?matchId=${matchData.matchId}`,
    );

    // Handle snapshot (full match data)
    eventSource.addEventListener("snapshot", (e) => {
      try {
        const data = JSON.parse((e as MessageEvent<string>).data) as Match;
        setMatchData(data);
        setIsSseErrorOpen(false);

        if (data.status === "waiting") setPhase("waiting");
        else if (data.status === "live") setPhase("live");
        else if (data.status === "completed") setPhase("completed");

        // if (data.status === "waiting") {
        //   // Only show pre-seeded bot (order 0) initially.
        //   // Remaining players arrive via player_joined events with animations and SFX.
        //   const preSeeded = data.players.filter(
        //     (p) => p.squadDetails.orderPlayer === 0,
        //   );
        //   setMatchData({ ...data, players: preSeeded });
        //   setPhase("waiting");
        // } else {
        //   // Live or completed — show all players immediately (reconnection case)
        //   setMatchData(data);
        //   if (data.status === "live") setPhase("live");
        //   else if (data.status === "completed") setPhase("completed");
        // }
      } catch (err) {
        console.error("Failed to parse practice snapshot:", err);
      }
    });

    // Handle player_joined (animated joins during lobby)
    eventSource.addEventListener("player_joined", (e) => {
      try {
        const { player } = JSON.parse(
          (e as MessageEvent<string>).data,
        ) as PracticePlayerJoinedEvent;

        setMatchData((prev) => {
          if (!prev) return prev;
          const exists = prev.players.some(
            (p) => p.squadAddress === player.squadAddress,
          );
          if (exists) return prev;
          return { ...prev, players: [...prev.players, player] };
        });
      } catch (err) {
        console.error("Failed to parse practice player_joined:", err);
      }
    });

    // Handle match_status_changed
    eventSource.addEventListener("match_status_changed", (e) => {
      try {
        const { new_status } = JSON.parse(
          (e as MessageEvent<string>).data,
        ) as PracticeMatchStatusChangedEvent;

        setMatchData((prev) => (prev ? { ...prev, status: new_status } : prev));
        if (new_status === "live") setPhase("live");
        else if (new_status === "completed") setPhase("completed");
      } catch (err) {
        console.error("Failed to parse practice match_status_changed:", err);
      }
    });

    // Handle errors
    const handleStreamError = () => {
      setIsSseErrorOpen(true);
    };
    eventSource.addEventListener("error", handleStreamError);
    eventSource.onerror = handleStreamError;

    return () => eventSource.close();
  }, [matchData?.matchId, phase]);

  // --- Play SFX when someone joins ---
  const currentPlayerCount =
    matchData?.players?.length ?? displayedPlayers.length;
  useEffect(() => {
    const current = currentPlayerCount;
    console.log(
      "[SFX effect] players:",
      current,
      "prev:",
      prevPlayersCountRef.current,
    );
    if (
      prevPlayersCountRef.current !== null &&
      current > prevPlayersCountRef.current
    ) {
      setIsPiggyActive(true);
      joinSoundRef.current?.play().catch((err) => {
        console.debug("Join sound blocked by autoplay policy:", err);
      });
      setTimeout(() => setIsPiggyActive(false), 3000);
    }
    prevPlayersCountRef.current = current;
  }, [currentPlayerCount]);

  // --- Callback when NavigationPractice finds a match ---
  const handleMatchFound = useCallback(
    (match: Match) => {
      console.log(
        "[handleMatchFound] matchId:",
        match.matchId,
        "displayedPlayers:",
        displayedPlayers.length,
      );
      setMatchData(match);
      setPhase("waiting");
    },
    [],
  );

  // --- Start practice tour (How It Works) ---
  const handleStartPracticeTour = useCallback(() => {
    const startTour = () => {
      try {
        closeNextStep();
        // eslint-disable-next-line no-empty
      } catch {}
      startNextStep("practiceTour");
    };

    const firstSelector = "[data-step='practice-controller']";
    const node = document.querySelector(firstSelector);

    if (node) {
      startTour();
      return;
    }

    setTimeout(() => {
      const retryNode = document.querySelector(firstSelector);
      if (retryNode) {
        startTour();
      }
    }, 300);
  }, [closeNextStep, startNextStep]);

  // --- Reset to idle ---
  const handleBackToIdle = () => {
    // If returning from lobby onboarding tour, redirect back to lobby for squad tour
    const lobbyPending = sessionStorage.getItem("cfl:lobby-tour-practice-pending");
    if (lobbyPending) {
      sessionStorage.removeItem("cfl:lobby-tour-practice-pending");
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.setItem("cfl:lobby-tour-squad-pending", "true");
      router.navigate({ to: "/home" });
      // Notify lobby to check for pending tours after navigation
      setTimeout(() => window.dispatchEvent(new CustomEvent("lobbyTourCheck")), 600);
      return;
    }

    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setMatchData(null);
    setDisplayedPlayers([]);
    setPhase("idle");
    prevPlayersCountRef.current = null;
  };

  // --- SSE error overlay ---
  const sseErrorOverlay = (
    <Activity mode={isSseErrorOpen ? "visible" : "hidden"}>
      <div className="fixed inset-0 z-1002! flex items-center justify-center bg-indigo-950/90 backdrop-blur-xs px-6 text-left">
        <div className="relative w-88 sm:w-[24rem] flex flex-col items-center gap-6 text-center overflow-x-visible">
          <div className="relative flex flex-col items-center justify-center w-full h-40 overflow-x-visible">
            <img
              src={"/assets/connection-error.png"}
              alt="Info"
              className="h-40 object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          </div>
          <div className="flex flex-col items-center">
            <div className="text-2xl font-bold z-100 uppercase">
              <AnimatedWaiting
                prefix="Reconnecting"
                className="text-white!"
                style={{ textShadow: generateTextShadow(1.5) }}
              />
            </div>
          </div>
        </div>
      </div>
    </Activity>
  );

  return (
    <div
      className="flex flex-col w-full h-full relative overflow-hidden bg-black"
      style={{ imageRendering: "pixelated" }}
    >
      {/* Top bar — always visible except when live */}
      <Activity mode={phase === "live" ? "hidden" : "visible"}>
        <ResourceBar
          className="absolute top-4 left-14 right-4 flex z-50 pl-3"
          rightExtra={
            phase !== "live" && phase !== "completed" ? (
              <HowItWorkButton
                size="size-8"
                onClick={handleStartPracticeTour}
              />
            ) : undefined
          }
        />
        <FloatingLeftButton
          onClick={() => {
            if (phase !== "idle") {
              handleBackToIdle();
            } else {
              router.navigate({ to: "/home" });
            }
          }}
          className="absolute left-0 top-4 z-50"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />
      </Activity>

      {/* Join SFX audio element */}
      <audio
        ref={joinSoundRef}
        src="https://cdn.navalabs.io/assets/bgm-join_NtqOlx.mp3"
        preload="auto"
      />

      {/* ============ IDLE STATE ============ */}
      {(phase === "idle" || (phase === "waiting" && matchData)) && (
        <>
          <div
            className="h-full max-h-[60svh] flex-1 flex flex-col justify-center"
            style={{
              backgroundImage:
                // "url(https://cdn.navalabs.io/assets/bg-rush_NLMfkn.PNG)",
                "url(https://cdn.navalabs.io/assets/bg-full_cJmDRn.png)",
              backgroundSize: "cover",
              backgroundPosition: "center top",
              backgroundRepeat: "no-repeat",
            }}
          >
            {/* Coin Piggy at center */}
            <div className="flex flex-col items-center gap-1 mt-20">
              <div className="w-[38%] sm:w-1/3 overflow-hidden">
                <img
                  src={
                    isPiggyActive
                      ? "https://cdn.navalabs.io/assets/coach-monke_iVXKD5.png"
                      : "https://cdn.navalabs.io/assets/coach-monke_iVXKD5.png"
                  }
                  style={{ imageRendering: "pixelated", objectFit: "cover" }}
                  alt="Coin Piggy"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>

            {/* Rolling prize display */}
            <div
              className="relative mx-8 scale-90 sm:scale-100"
              data-step="practice-prizes"
            >
              <img
                alt="Rush reel frame"
                className="relative w-full z-10 pointer-events-none"
                src="https://cdn.navalabs.io/rolling_list_frame_xokld8.png"
              />
              <RollingList
                prizeData={prizeData}
                entryUsdc={practiceEntryFee}
                solPrice={solPrice}
                isPractice={true}
              />
            </div>
          </div>

          {phase === "waiting" && matchData ? (
            <MatchWaiting
              match={matchData}
              className="h-full max-h-[40svh]"
            />
          ) : (
            <NavigationPractice
              isFindMatchDisabled={false}
              onMatchFound={handleMatchFound}
              className="h-full max-h-[40svh]"
            />
          )}
        </>
      )}

      {/* ============ LIVE STATE ============ */}
      {phase === "live" && matchData && <PracticeLive match={matchData} />}

      {/* ============ COMPLETED STATE ============ */}
      {phase === "completed" && matchData && (
        <PracticeEnded
          match={matchData}
          handleBackToIdle={handleBackToIdle}
          isOnboardingMission={userStats?.isOnboardingMission}
        />
      )}

      {/* SSE Error overlay */}
      {sseErrorOverlay}

      {/* Report dialog */}
      <ReportPlayerDialog
        open={reportTarget !== null}
        onOpenChange={(open) => {
          if (!open) setReportTarget(null);
        }}
        reportedId={reportTarget?.ownerProfile.userId}
        reportedUsername={reportTarget?.ownerProfile.username}
        reportedProfilePicture={reportTarget?.ownerProfile.profilePicture}
        reportedFrameUrl={reportTarget?.ownerProfile.activeFrameUrl}
        reportedIsVerified={reportTarget?.ownerProfile.isVerified}
        canReportPlayer={practiceCanReportMap.get(reportTarget?.ownerProfile.userId ?? -1) ?? false}
        proofId={matchData?.matchId}
        playerDetails={reportTarget?.squadDetails.playerDetails}
      />
    </div>
  );
}
